import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { EnrichmentDataChip } from "./EnrichmentDataChip";
import { AttributeInfoIcon } from "./enrichmentTooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import LocationAutocomplete from "./LocationAutocomplete";
import { SuggestField } from "./SuggestField";
import GeospatialRiskMap from "./GeospatialRiskMap";
import ThreatIntelligencePanel from "./ThreatIntelligencePanel";
import AISuggestionsBanner from "./AISuggestionsBanner";
import { clearEnrichmentContext } from "../utils/enrichmentContext";
import { toast } from "sonner";
import {
  Sparkles, Check, ChevronLeft, ChevronRight, Plus, Trash2, X,
  Building2, MapPin, Users, AlertTriangle, Globe, Phone, BadgeCheck,
  Loader2, Clock, Target, Layers, Zap, ShieldCheck, TrendingUp,
  Shield, Factory, Heart, Cpu, Briefcase, Landmark, Network,
  Radio, Truck, GraduationCap, ShoppingCart, Hotel, ScrollText,
  Server, Lock, Wheat, FlaskConical, Microscope, Swords, SkipForward,
  Dumbbell, HelpCircle, Car, Leaf, Rocket, Radiation, CheckCircle2,
  TreePine, Anchor, CircuitBoard, Gavel, ArrowRight, RotateCcw, ChevronDown, Pencil,
} from "lucide-react";

/* ────────────────────────────────────────────
   Sector / Sub-Industry icons
   ──────────────────────────────────────────── */
const SECTOR_ICONS = {
  financial_services: Landmark, healthcare: Heart, manufacturing: Factory,
  infrastructure: Radio, public_service: GraduationCap, technology: Cpu,
  mining_resources: Factory, real_estate_construction: Building2,
  media_entertainment: Radio, nonprofit_ngo: Heart, professional_services: Briefcase,
  transportation: Truck, agriculture_agribusiness: Wheat, chemical_materials: FlaskConical,
  life_sciences_biotech: Microscope, defense_security: Swords, sports_leisure: Dumbbell,
  automotive_ev: Car, cannabis_regulated: Leaf, space_satellite: Rocket,
  nuclear_radiation: Radiation, insurance_services: ShieldCheck,
  environmental_services: TreePine, maritime_ports: Anchor,
  semiconductor_chips: CircuitBoard, other: HelpCircle,
};

const SUB_INDUSTRY_ICONS = {
  banking: Landmark, fintech: Zap, asset_management: Target, insurance: Shield,
  clinical: Heart, medical_devices: Cpu, pharmaceuticals: Layers, health_tech: Globe,
  saas: Globe, data_centers: Server, msp: Shield, cybersecurity: Lock,
};

/* NAICS-derived sector label (from backend) → wizard taxonomy sector key. */
const SECTOR_LABEL_TO_KEY = {
  "Financial Services": "financial_services",
  Healthcare: "healthcare",
  Manufacturing: "manufacturing",
  Technology: "technology",
  Mining: "mining_resources",
  "Real Estate & Construction": "real_estate_construction",
  "Real Estate": "real_estate_construction",
  "Media & Entertainment": "media_entertainment",
  "Professional Services": "professional_services",
  Transportation: "transportation",
  Agriculture: "agriculture_agribusiness",
  Utilities: "infrastructure",
  Education: "public_service",
  Government: "public_service",
  Retail: "other",
  Hospitality: "other",
  Other: "other",
};

const HIERARCHY_LABELS = {
  government: "Government Agency", nonprofit: "Nonprofit / NGO",
  educational_institution: "Educational Institution", healthcare_system: "Healthcare System",
  franchise: "Franchise", cooperative: "Cooperative / Member-Owned",
  network_virtual: "Network / Virtual Org", geographic_multinational: "Geographic Multinational",
  holding_company: "Holding Company", divisional_subsidiary: "Divisional / Subsidiary",
  flat_startup: "Flat / Startup", matrix: "Matrix Organization", bu_division: "Business-Unit Division",
};

const INVITATION_MODES = [
  { id: "ai_suggested", label: "AI-Suggested", desc: "Use the recommended invitation sequence" },
  { id: "skip_levels", label: "Skip Levels", desc: "Bypass intermediate hierarchy levels" },
  { id: "direct_to_function", label: "Direct-to-Function", desc: "Invite function owners directly" },
  { id: "cherry_pick", label: "Cherry-Pick", desc: "Hand-pick specific people to invite" },
  { id: "parallel_waves", label: "Parallel Waves", desc: "Invite multiple levels simultaneously" },
  { id: "fully_custom", label: "Fully Custom", desc: "Configure every wave manually" },
];

const STEPS = [
  { id: 0, title: "Identity", description: "Discover your org" },
  { id: 1, title: "Industry", description: "Confirm classification" },
  { id: 2, title: "Profile", description: "Org & locations" },
  { id: 3, title: "Structure", description: "Hierarchy & invites" },
  { id: 4, title: "Regulatory", description: "Frameworks" },
  { id: 5, title: "AI Suggestions", description: "Departments" },
  { id: 6, title: "Ready", description: "Launch BCM" },
];

const SOURCE_LABELS = {
  sec_edgar: "SEC EDGAR", propublica: "ProPublica", context_dev: "Context.dev",
  pdl: "People Data Labs", google_kg: "Google KG", ai_recon: "AI Recon",
  cache: "Cache (30-day)", sandbox: "Sandbox", manual: "Manual",
  rules_engine: "Rules Engine",
};

const FIN_HEALTH = {
  healthy: { label: "Healthy", cls: "bg-emerald-100 text-emerald-800" },
  caution: { label: "Caution", cls: "bg-amber-100 text-amber-800" },
  concern: { label: "Concern", cls: "bg-red-100 text-red-800" },
  insufficient_data: { label: "Insufficient Data", cls: "bg-slate-100 text-slate-600" },
};

const ENTITY_TYPE_OPTIONS = ["Public", "Private", "Nonprofit", "Government", "Educational"];

function normalizeEntityType(v) {
  if (!v) return "";
  const s = String(v).toLowerCase();
  if (s.includes("public")) return "Public";
  if (s.includes("private")) return "Private";
  if (s.includes("nonprofit") || s.includes("non-profit") || s.includes("ngo")) return "Nonprofit";
  if (s.includes("government") || s.includes("agency")) return "Government";
  if (s.includes("education")) return "Educational";
  return String(v).replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Case-insensitive dedupe for string or {name}/{company_id} list items → string[]. */
function dedupeCI(arr) {
  const seen = new Set();
  const out = [];
  for (const item of (arr || [])) {
    const val = typeof item === "string" ? item : (item?.name || item?.company_id || "");
    const key = String(val).trim().toLowerCase();
    if (val && !seen.has(key)) { seen.add(key); out.push(val); }
  }
  return out;
}

/** Format a large USD revenue figure → "$245.1B" / "$12.3M". */
function formatRevenue(val) {
  if (val == null || isNaN(val)) return null;
  const n = Number(val);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

/* Tiny "via SEC EDGAR · 90%" provenance caption. */
function ProvenanceTag({ prov, className = "" }) {
  if (!prov?.label) return null;
  const conf = Number.isFinite(prov.confidence) ? ` · ${Math.round(prov.confidence * 100)}%` : "";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] text-slate-400 ${className}`}
      title={`Source: ${prov.label}${conf ? ` (confidence${conf})` : ""}`}
      data-testid="provenance-tag"
    >
      <BadgeCheck className="w-3 h-3 text-emerald-500" /> via {prov.label}{conf}
    </span>
  );
}

export default function SmartOnboardingWizard({ onComplete, initialData = {}, mode = "full" }) {
  const { api, user } = useAuth();
  const orgId = user?.org_id;
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Taxonomy
  const [taxonomy, setTaxonomy] = useState({});

  // Enrichment (Step 0)
  const [enrichMode, setEnrichMode] = useState(null);      // null | "auto" | "manual"
  const [identityChoice, setIdentityChoice] = useState(null); // null | "auto" | "manual" (which button was clicked)
  const [enrichStatus, setEnrichStatus] = useState("idle"); // idle | running | complete | error
  const [enrichProgress, setEnrichProgress] = useState([]);
  const [sourceStatus, setSourceStatus] = useState(null);  // {sec_edgar:'ok', context_dev:'key_error', ...}
  const [enrichment, setEnrichment] = useState(null);       // full /enrichment/org response
  const pollRef = useRef(null);
  const triggeredRef = useRef(false);
  const skipRef = useRef(false);
  const lastCountRef = useRef(0);

  // Organization data
  const [orgData, setOrgData] = useState({
    name: initialData.name || "",
    sector: initialData.sector || "",
    sub_industry: initialData.sub_industry || initialData.industry || "",
    sub_industries: initialData.sub_industries || (initialData.sub_industry ? [initialData.sub_industry] : []),
    phone: initialData.phone || "",
    website: initialData.website || "",
    // Additive enrichment-backed identity/profile/structure fields (all editable)
    entity_type: initialData.entity_type || "",
    duns_number: initialData.duns_number || "",
    year_founded: initialData.year_founded || "",
    company_summary: initialData.company_summary || "",
    parent_company: initialData.parent_company || "",
    // User-editable primary industry codes (Step "Industry").
    naics_primary: initialData.naics_primary || "",
    sic_primary: initialData.sic_primary || "",
    // B1/B3 additive manual-entry fields.
    ein: initialData.ein || "",
    employee_count: initialData.employee_count || "",
    tech_stack: initialData.tech_stack || [],
    annual_revenue: initialData.annual_revenue || "",
    revenue_fiscal_year: initialData.revenue_fiscal_year || "",
    certifications: initialData.certifications || [],
  });

  // Additive editable enrichment lists (DBA/alt names, products, subsidiaries,
  // divisions). Seeded once from enrichment; user edits are preserved via
  // touchedRef so progressive polling never re-adds removed items.
  const [dbaNames, setDbaNames] = useState([]);
  const [products, setProducts] = useState(initialData.products_services || []);
  const [subsidiaries, setSubsidiaries] = useState(initialData.subsidiaries || []);
  const [divisions, setDivisions] = useState([]);
  const [newDba, setNewDba] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const [newSub, setNewSub] = useState("");
  const [newDivision, setNewDivision] = useState("");
  const [newTech, setNewTech] = useState("");
  const [certForm, setCertForm] = useState({ type: "ISO 22301", name: "", certificate_number: "", issuing_body: "", expiry: "" });
  const CERT_TYPES = ["ISO 22301", "ISO 27001", "ISO 9001", "SOC 2", "Minority-owned (MBE)", "Women-owned (WBE)", "Veteran-owned", "Small Business", "Other"];
  const addCert = () => {
    if (certForm.type === "Other" && !certForm.name.trim()) { toast.error("Name required when type is Other"); return; }
    setOrgData((p) => ({ ...p, certifications: [...(p.certifications || []), { ...certForm }] }));
    setCertForm({ type: "ISO 22301", name: "", certificate_number: "", issuing_body: "", expiry: "" });
  };
  const removeCert = (i) => setOrgData((p) => ({ ...p, certifications: p.certifications.filter((_, x) => x !== i) }));
  const isExpired = (d) => d && new Date(d) < new Date();
  const [naicsOpen, setNaicsOpen] = useState(false);
  const [sicOpen, setSicOpen] = useState(false);
  const touchedRef = useRef({});

  // Seed an editable list once; skips if the user has already touched it so
  // progressive enrichment polling never re-adds a removed item.
  const seedList = (key, setter, values) => {
    if (touchedRef.current[key]) return;
    if (values && values.length) setter(values);
  };
  const markTouched = (key) => { touchedRef.current[key] = true; };

  // Always start a wizard session with a clean Headquarters slate so stale
  // locations from a shared/sandbox org never leak in. Real locations are
  // populated from enrichment (operating_locations) or entered by the user, and
  // completion is additive — existing saved locations are not lost.
  const [locations, setLocations] = useState(
    (initialData.locations && initialData.locations.length > 0)
      ? initialData.locations.map((l, i) => {
          // B8 — legacy sites store only a single-line `address`; parse it into
          // the structured inputs (same end-parse as the B6 create path) so
          // re-opening the wizard shows the saved location, not blanks.
          const parts = String(l.address || "").split(",").map(s => s.trim()).filter(Boolean);
          const n = parts.length;
          let pStreet = "", pCity = "", pState = "", pCountry = "";
          if (n >= 4) { pStreet = parts[0]; pCity = parts[1]; pState = parts[2]; pCountry = parts[n - 1]; }
          else if (n === 3) { pStreet = parts[0]; pCity = parts[1]; pCountry = parts[2]; }
          else if (n === 2) { pStreet = parts[0]; pCity = parts[1]; }
          else if (n === 1) { pStreet = parts[0]; }
          return {
            id: l.id || i + 1,
            name: l.name || (l.type === "headquarters" ? "Headquarters" : `Site ${i + 1}`),
            address: l.address || "",
            latitude: l.latitude ?? null,
            longitude: l.longitude ?? null,
            type: l.type || (i === 0 ? "headquarters" : "branch"),
            function: l.function || "",
            phone: l.phone || "",
            street: l.street || pStreet,
            city: l.city || pCity,
            state: l.state || pState,
            postal_code: l.postal_code || "",
            country: l.country || pCountry,
            source: "saved",
          };
        })
      : [{ id: 1, name: "Headquarters", address: "", latitude: null, longitude: null, type: "headquarters" }]
  );
  const [locationRisks, setLocationRisks] = useState({});

  // Departments
  const [departments, setDepartments] = useState(initialData.departments || []);
  const [suggestedDepartments, setSuggestedDepartments] = useState([]);
  const [deptInitialized, setDeptInitialized] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // AI sub-industry detection (Step 1)
  const [subInd, setSubInd] = useState({ loading: false, rationale: "" });

  // Accepted AI Critical Business Functions (Step 5)
  const [acceptedFunctions, setAcceptedFunctions] = useState([]);

  // Accepted AI module suggestions (Step 5) — Mission/Vision, Stakeholders, Infrastructure
  const [acceptedMissionVision, setAcceptedMissionVision] = useState([]);
  const [acceptedStakeholders, setAcceptedStakeholders] = useState([]);
  const [acceptedInfrastructure, setAcceptedInfrastructure] = useState([]);

  // AI Recon
  const [aiRecon, setAiRecon] = useState(null);
  const [aiReconLoading, setAiReconLoading] = useState(false);

  // Hierarchy (Step 3)
  const [hierarchy, setHierarchy] = useState({ candidates: [], selected: "", loading: false });
  const [invitationMode, setInvitationMode] = useState("ai_suggested");
  const [showAdvancedModes, setShowAdvancedModes] = useState(false);

  // Frameworks (Step 4)
  const [frameworks, setFrameworks] = useState(null);
  const [fwLoading, setFwLoading] = useState(false);

  const provenance = enrichment?.provenance || {};

  /* ── Fetch taxonomy once ── */
  useEffect(() => {
    api.get("/ai-assistant/industry-taxonomy")
      .then((res) => res.data?.taxonomy && setTaxonomy(res.data.taxonomy))
      .catch((e) => console.error("taxonomy load failed", e));
  }, [api]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  /* ── Apply enrichment results to wizard state ── */
  const applyEnrichment = useCallback((data) => {
    setEnrichment(data);
    const ed = data?.enrichment_data || {};
    const cd = data?.classification_data || {};
    const ci = data?.compliance_identifiers || {};
    const oh = data?.org_hierarchy || {};

    setOrgData((prev) => {
      const next = { ...prev };
      if (ed.legal_name && !prev.name) next.name = ed.legal_name;
      if (ed.domain && !prev.website) next.website = ed.domain;
      // NAICS → sector auto-select
      const sectorKey = SECTOR_LABEL_TO_KEY[cd.naics_to_sector_mapping];
      if (sectorKey && !prev.sector) next.sector = sectorKey;
      // Pre-select AI-detected sub-industries (cached on classification_data)
      const detected = (cd.detected_sub_industries || []).map((d) => d.key).filter(Boolean);
      if (detected.length && prev.sub_industries.length === 0) {
        next.sub_industries = detected;
        next.sub_industry = detected[0];
      }
      // Additive scalar fields — fill-only, so user edits persist across polls.
      if (!prev.entity_type && (ed.entity_type || ed.public_private)) {
        next.entity_type = normalizeEntityType(ed.entity_type || ed.public_private);
      }
      if (!prev.duns_number && ci.duns_number) next.duns_number = ci.duns_number;
      // Fill-only + touch-guarded so a re-run never overwrites a user-entered code.
      if (!touchedRef.current.naics_primary && !prev.naics_primary && cd.naics_primary) next.naics_primary = String(cd.naics_primary);
      if (!touchedRef.current.sic_primary && !prev.sic_primary && cd.sic_primary) next.sic_primary = String(cd.sic_primary);
      if (!prev.year_founded && ed.year_founded) next.year_founded = String(ed.year_founded);
      if (!prev.company_summary && ed.company_summary) next.company_summary = ed.company_summary;
      if (!prev.parent_company) {
        const parent = oh.parent_name_wikidata || oh.parent_name_ai_recon
          || oh.immediate_parent || oh.ultimate_parent || oh.ultimate_parent_ticker;
        if (parent) next.parent_company = String(parent);
      }
      return next;
    });

    // Seed editable lists once (never re-add user-removed items).
    seedList("dba", setDbaNames, dedupeCI([...(ed.alternate_names || []), ...(ed.dba_names || [])]));
    seedList("products", setProducts, dedupeCI(ed.products_services || []));
    seedList("subs", setSubsidiaries, dedupeCI([...(ed.subsidiary_names || []), ...(ed.detected_subsidiaries || [])]));
    seedList("divisions", setDivisions, dedupeCI(ed.divisions || []));

    // Pre-fill locations from HQ + operating_locations
    const locs = [];
    if (ed.headquarters?.city) {
      locs.push({
        id: 1, name: "Headquarters", type: "headquarters",
        address: [ed.headquarters.city, ed.headquarters.state, ed.headquarters.country].filter(Boolean).join(", "),
        latitude: ed.headquarters.lat ?? null, longitude: ed.headquarters.lng ?? null, source: "enrichment",
      });
    }
    (ed.operating_locations || []).forEach((l) => {
      if (l.city || l.locality) locs.push({
        id: locs.length + 1, name: `${l.city || l.locality} Office`, type: "branch",
        address: [l.city || l.locality, l.state || l.region, l.country].filter(Boolean).join(", "),
        latitude: l.lat ?? null, longitude: l.lng ?? null, source: "enrichment",
      });
    });
    if (locs.length) {
      // Only auto-fill while the user hasn't entered/edited any location.
      setLocations((prev) => {
        const untouched = prev.length <= 1 && !prev[0]?.address && !prev[0]?.latitude;
        return untouched ? locs : prev;
      });
    }
  }, []);

  const [step0Errors, setStep0Errors] = useState({});

  /* ── Step 0 mandatory-field validation (name + website, non-whitespace) ── */
  const validateStep0 = useCallback(() => {
    const errs = {};
    if (!orgData.name.trim()) errs.name = "Organization Name is required";
    if (!orgData.website.trim()) errs.website = "Company Website URL is required";
    setStep0Errors(errs);
    return Object.keys(errs).length === 0;
  }, [orgData.name, orgData.website]);

  const finishEnrichment = useCallback(async () => {
    try {
      const res = await api.get(`/enrichment/org/${orgId}`);
      applyEnrichment(res.data);
    } catch (e) {
      console.error("fetch enrichment failed", e);
    }
    setEnrichStatus("complete");
    toast.success("Profile auto-discovered — review and adjust as needed");
  }, [api, orgId, applyEnrichment]);

  /* ── Trigger enrichment waterfall + poll status ── */
  const startEnrichment = useCallback(async () => {
    if (!orgId || enrichStatus === "running") return;
    // If a Skip click is what triggered the website onBlur, suppress the
    // blur-driven auto-discover so the Skip click can advance to manual setup.
    if (skipRef.current) { skipRef.current = false; return; }
    if (!validateStep0()) return;
    const domain = orgData.website.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    // Detect an in-place domain change vs the last-enriched domain. A domain
    // change must bypass the "already triggered" latch so re-discovery runs.
    const norm = (d) => (d || "").toString().trim().toLowerCase()
      .replace(/^https?:\/\//, "").split("/")[0].replace(/^www\./, "");
    const prevDomain = norm(enrichment?.enrichment_data?.domain);
    const nextDomain = norm(domain);
    const domainChanged = !!(prevDomain && nextDomain && prevDomain !== nextDomain);
    if (triggeredRef.current && !domainChanged) return;
    triggeredRef.current = true;
    lastCountRef.current = 0;
    setEnrichMode("auto");
    setEnrichStatus("running");
    setEnrichProgress(["Querying SEC EDGAR & ProPublica…"]);
    // If the domain changed, wipe the previous company's data FIRST (backend +
    // local state) so no stale chips/suggestions from the old domain bleed
    // through — even if the user edited the URL without using "Start Over".
    if (domainChanged) {
      try {
        const res = await api.post(`/enrichment/clear/${orgId}`);
        console.log(`[DomainChange] cleared enrichment (${prevDomain} → ${nextDomain}):`, res.data);
        clearEnrichmentContext(orgId);
        toast.info("Domain changed — previous auto-discovered data cleared.");
      } catch (e) {
        console.error("[DomainChange] clear failed", e);
      }
      touchedRef.current = {};
      setEnrichment(null);
      setSourceStatus(null);
      setDbaNames([]); setProducts([]); setSubsidiaries([]); setDivisions([]);
      // Reset enrichment-derived org fields (keep the name/website the user just
      // typed) so the new domain's enrichment repopulates sector/industry etc.
      setOrgData((prev) => ({
        ...prev, sector: "", sub_industry: "", sub_industries: [],
        entity_type: "", duns_number: "", year_founded: "", company_summary: "", parent_company: "",
        naics_primary: "", sic_primary: "",
      }));
    }
    try {
      await api.post("/enrichment/enrich-org", {
        org_id: orgId, domain: domain || null, company_name: orgData.name.trim(),
      });
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const res = await api.get(`/enrichment/status/${orgId}`);
          const s = res.data;
          if (s.data_source_status) setSourceStatus(s.data_source_status);
          setEnrichProgress((p) => [...new Set([
            ...p, ...(s.sources_queried || []).map((src) => `Auto-discovered from ${SOURCE_LABELS[src] || src}`),
          ])]);
          // Progressive disclosure — pull partial enrichment as soon as a tier
          // checkpoint adds new fields, so identity/HQ/sector render before the
          // slower AI financial-health + Tier-2 NAICS steps finish.
          const count = (s.fields_populated || []).length;
          if (count > lastCountRef.current) {
            lastCountRef.current = count;
            try { applyEnrichment((await api.get(`/enrichment/org/${orgId}`)).data); } catch { /* keep polling */ }
          }
          if (s.status === "complete" || s.status === "error" || tries > 14) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            await finishEnrichment();
          }
        } catch { /* keep polling */ }
      };
      pollRef.current = setInterval(poll, 3000);
      poll();
    } catch (e) {
      triggeredRef.current = false;
      setEnrichStatus("error");
      toast.error(e.response?.data?.detail || "Auto-discovery failed to start");
    }
  }, [api, orgId, orgData.name, orgData.website, enrichStatus, finishEnrichment, enrichment, validateStep0]);

  // "Enter details myself" — reveal the manual fields (incl. D-U-N-S) inline on
  // the Identity step WITHOUT auto-advancing, so the user can fill them before
  // moving on. No discovery call. Advancing to Industry is via the Next button.
  const chooseManual = useCallback(() => {
    skipRef.current = false;
    if (!validateStep0()) return;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setEnrichMode("manual");
    setEnrichStatus("idle");
    setIdentityChoice("manual");
  }, [validateStep0]);

  /* ── AI Recon website scan (Step 2 prefill) ── */
  const runAiRecon = useCallback(async () => {
    if (!orgData.website || aiReconLoading || aiRecon) return;
    setAiReconLoading(true);
    try {
      const res = await api.post("/ai-recon/website-scan", { website_url: orgData.website });
      setAiRecon(res.data || {});
    } catch {
      setAiRecon({ status: "error", offices: [], departments: [] });
    } finally {
      setAiReconLoading(false);
    }
  }, [api, orgData.website, aiReconLoading, aiRecon]);

  /* ── AI sub-industry detection (Step 1) ── */
  const detectSubIndustry = useCallback(async (force = false) => {
    if (!orgId || !orgData.sector) return;
    if (!force && orgData.sub_industries.length > 0) return;
    setSubInd({ loading: true, rationale: "" });
    try {
      const res = await api.post("/enrichment/detect-sub-industry", { org_id: orgId, sector: orgData.sector });
      const keys = (res.data?.detected_sub_industries || []).map((d) => d.key).filter(Boolean);
      if (keys.length) {
        setOrgData((prev) => (!force && prev.sub_industries.length > 0)
          ? prev
          : { ...prev, sub_industries: keys, sub_industry: keys[0] });
      }
      setSubInd({ loading: false, rationale: res.data?.rationale || "" });
      if (force) toast.success(keys.length ? "AI re-detected your sub-industries" : "No sub-industry match found");
    } catch {
      setSubInd({ loading: false, rationale: "" });
      if (force) toast.error("Sub-industry detection failed");
    }
  }, [api, orgId, orgData.sector, orgData.sub_industries.length]);

  /* ── Capture accepted AI Critical Business Functions (Step 5) ── */
  const handleAcceptFunctions = useCallback((items) => {
    const norm = (items || []).map((it) => (typeof it === "object" && it !== null)
      ? { function_name: it.function_name || it.name || it.label, department: it.department, estimated_mtpd: it.estimated_mtpd }
      : { function_name: String(it) });
    setAcceptedFunctions((prev) => {
      const seen = new Set(prev.map((p) => p.function_name));
      return [...prev, ...norm.filter((n) => n.function_name && !seen.has(n.function_name))];
    });
    toast.success(`${norm.length} critical business function(s) added to your program`);
  }, []);

  /* ── Capture accepted AI module suggestions (Step 5) — generic ── */
  const makeModuleAcceptor = useCallback((setter, labelFor) => (items) => {
    const norm = (items || []).map((it) => (typeof it === "object" && it !== null)
      ? it
      : { label: String(it) });
    setter((prev) => {
      const keyOf = (x) => (x.name || x.label || x.mission || x.vision || JSON.stringify(x));
      const seen = new Set(prev.map(keyOf));
      return [...prev, ...norm.filter((n) => !seen.has(keyOf(n)))];
    });
    toast.success(`${norm.length} ${labelFor} added to your program`);
  }, []);

  const handleAcceptMissionVision = useCallback((items) => makeModuleAcceptor(setAcceptedMissionVision, "mission/vision statement(s)")(items), [makeModuleAcceptor]);
  const handleAcceptStakeholders = useCallback((items) => makeModuleAcceptor(setAcceptedStakeholders, "stakeholder(s)")(items), [makeModuleAcceptor]);
  const handleAcceptInfrastructure = useCallback((items) => makeModuleAcceptor(setAcceptedInfrastructure, "application(s)")(items), [makeModuleAcceptor]);

  /* ── Hierarchy detection (Step 3) ── */
  const detectHierarchy = useCallback(async () => {
    if (!orgId) return;
    setHierarchy((h) => ({ ...h, loading: true }));
    try {
      const res = await api.post("/enrichment/detect-hierarchy", { org_id: orgId });
      const cands = res.data?.detected_models || [];
      setHierarchy({ candidates: cands, selected: cands[0]?.model || "flat_startup", loading: false });
    } catch {
      setHierarchy({ candidates: [{ model: "flat_startup", confidence: 0.6 }], selected: "flat_startup", loading: false });
    }
  }, [api, orgId]);

  /* ── Framework detection (Step 4) ── */
  const detectFrameworks = useCallback(async () => {
    if (!orgId) return;
    setFwLoading(true);
    try {
      const res = await api.post("/compliance-reports/auto-detect-frameworks", { org_id: orgId });
      setFrameworks(res.data);
    } catch {
      setFrameworks({ mandatory_frameworks: ["ISO 22301", "ISO 27001", "NIST CSF 2.0"], recommended_frameworks: [], sector_specific: [], rationale: [] });
    } finally {
      setFwLoading(false);
    }
  }, [api, orgId]);

  /* ── Department suggestions (Step 5) ── */
  const fetchSuggestedDepartments = useCallback(async () => {
    if (orgData.sub_industries.length === 0) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ai-assistant/predict-business-units", {
        industry_type: orgData.sector || orgData.sub_industries[0],
        sub_industry: orgData.sub_industries[0],
        sub_industries: orgData.sub_industries,
        org_name: orgData.name,
      });
      if (res.data?.success) {
        const suggestions = res.data.suggested_departments || [];
        setSuggestedDepartments(suggestions);
        // Make the AI suggestions (for the CORRECT industry) authoritative on first
        // entry. Auto-select the critical ones and preserve only pre-existing
        // departments that also appear in the suggestions — this drops any stale /
        // wrong-template departments (e.g. an insurance default) so the Step-5
        // selection is exactly what carries through to the final review + creation.
        if (!deptInitialized) {
          setDepartments((prev) => {
            const names = new Set(suggestions.map((s) => s.name));
            const autoSelected = suggestions
              .filter((d) => (d.criticality || "").toLowerCase() === "critical")
              .map((d) => ({ ...d, selected: true }));
            const preserved = prev.filter((d) => names.has(d.name));
            const byName = new Map();
            [...autoSelected, ...preserved].forEach((d) => byName.set(d.name, d));
            let next = [...byName.values()];
            if (next.length === 0 && suggestions.length > 0) {
              next = suggestions.slice(0, 5).map((d) => ({ ...d, selected: true }));
            }
            return next;
          });
          setDeptInitialized(true);
        }
      }
    } catch (e) {
      console.error("dept suggestions failed", e);
    } finally {
      setAiLoading(false);
    }
  }, [api, orgData.sector, orgData.sub_industries, orgData.name, deptInitialized]);

  /* ── Lazy-load per step ── */
  useEffect(() => {
    if (currentStep === 2) runAiRecon();
    if (currentStep === 3 && hierarchy.candidates.length === 0 && !hierarchy.loading) detectHierarchy();
    if (currentStep === 4 && !frameworks && !fwLoading) detectFrameworks();
    if (currentStep === 5 && suggestedDepartments.length === 0) fetchSuggestedDepartments();
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── AI sub-industry detection when entering/changing sector on Step 1 ── */
  useEffect(() => {
    if (currentStep === 1 && enrichment && orgData.sector && orgData.sub_industries.length === 0) {
      detectSubIndustry();
    }
  }, [currentStep, orgData.sector]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── AI-Recon industry fallback: when no NAICS→sector mapping exists (private
        companies), pre-select the sector from AI Recon's free-text ai_sector. ── */
  useEffect(() => {
    if (!enrichment || orgData.sector) return;
    const c = enrichment.classification_data || {};
    if (c.naics_to_sector_mapping) return;
    const aiSector = c.ai_sector;
    if (aiSector && Object.keys(taxonomy).length) {
      const a = String(aiSector).toLowerCase();
      const match = Object.entries(taxonomy).find(([, info]) => {
        const l = String(info.label || "").toLowerCase();
        return l && (l.includes(a) || a.includes(l));
      });
      if (match) setOrgData((p) => (p.sector ? p : { ...p, sector: match[0] }));
    }
  }, [enrichment, taxonomy]); // eslint-disable-line react-hooks/exhaustive-deps


  /* ── Persist hierarchy + invitation mode when leaving Step 3 ── */
  const persistStructure = useCallback(async () => {
    if (!orgId || !hierarchy.selected) return;
    try {
      await api.post("/enrichment/confirm-hierarchy", { org_id: orgId, confirmed_model: hierarchy.selected });
      await api.post("/enrichment/customize-invitation-flow", { org_id: orgId, invitation_mode: invitationMode });
    } catch (e) {
      console.error("persist structure failed", e);
    }
  }, [api, orgId, hierarchy.selected, invitationMode]);

  /* ── Locations helpers ── */
  const handleLocationSelect = (index, d) => {
    const updated = [...locations];
    updated[index] = { ...updated[index], address: d.address, latitude: d.latitude, longitude: d.longitude, details: d.details };
    setLocations(updated);
  };
  const addLocation = () => setLocations([...locations, { id: Date.now(), name: `Office ${locations.length + 1}`, address: "", latitude: null, longitude: null, type: "branch" }]);
  const removeLocation = (i) => locations.length > 1 && setLocations(locations.filter((_, idx) => idx !== i));
  const handleLocationRiskAnalysis = (loc, analysis) => setLocationRisks((p) => ({ ...p, [loc.id]: analysis }));

  const toggleDepartment = (dept) => {
    setDepartments(departments.some((d) => d.name === dept.name)
      ? departments.filter((d) => d.name !== dept.name)
      : [...departments, { ...dept, selected: true }]);
  };

  /* ── Complete ── */
  const handleComplete = async () => {
    setLoading(true);
    try {
      const payload = {
        organization: {
          ...orgData,
          industry: orgData.sub_industries[0] || orgData.sub_industry || orgData.sector,
          industry_metadata: { sector: orgData.sector, sub_industry: orgData.sub_industries[0] || orgData.sub_industry, sub_industries: orgData.sub_industries },
        },
        locations: locations.filter((l) => l.address),
        departments,
        location_risks: locationRisks,
        hierarchy_model: hierarchy.selected,
        invitation_mode: invitationMode,
        critical_functions: acceptedFunctions,
        mission_vision: acceptedMissionVision,
        stakeholders: acceptedStakeholders,
        infrastructure_applications: acceptedInfrastructure,
        // Additive enrichment-backed fields (editable in wizard)
        dba_names: dbaNames,
        products_services: products,
        subsidiaries,
        divisions,
        // User-entered industry codes / D-U-N-S — only the values the user typed
        // by hand are flagged manual (auto-discovered values are left untouched).
        manual_codes: {
          ...(touchedRef.current.naics_primary && orgData.naics_primary ? { naics_primary: orgData.naics_primary } : {}),
          ...(touchedRef.current.sic_primary && orgData.sic_primary ? { sic_primary: orgData.sic_primary } : {}),
          ...(touchedRef.current.duns_number && orgData.duns_number ? { duns_number: orgData.duns_number } : {}),
          ...(touchedRef.current.ein && orgData.ein ? { ein: orgData.ein.replace(/[^0-9]/g, "") } : {}),
          ...(touchedRef.current.employee_count && orgData.employee_count ? { employee_count: parseInt(orgData.employee_count, 10) } : {}),
          ...(touchedRef.current.tech_stack && orgData.tech_stack?.length ? { tech_stack: orgData.tech_stack } : {}),
          ...(touchedRef.current.annual_revenue && orgData.annual_revenue ? { annual_revenue: parseFloat(orgData.annual_revenue), revenue_fiscal_year: orgData.revenue_fiscal_year ? parseInt(orgData.revenue_fiscal_year, 10) : undefined } : {}),
          ...(orgData.certifications?.length ? { certifications: orgData.certifications } : {}),
        },
      };
      // Persist accepted Critical Business Functions (BIA seed) before finishing.
      if (acceptedFunctions.length && orgId) {
        try { await api.post("/enrichment/save-bia-seed", { org_id: orgId, critical_functions: acceptedFunctions }); }
        catch (e) { console.error("save bia-seed failed", e); }
      }
      // Persist accepted Mission/Vision, Stakeholders & Infrastructure suggestions.
      if (orgId && (acceptedMissionVision.length || acceptedStakeholders.length || acceptedInfrastructure.length)) {
        try {
          await api.post("/enrichment/save-onboarding-extras", {
            org_id: orgId,
            mission_vision: acceptedMissionVision,
            stakeholders: acceptedStakeholders,
            infrastructure_applications: acceptedInfrastructure,
          });
        } catch (e) { console.error("save onboarding extras failed", e); }
      }
      await onComplete?.(payload);
    } catch (e) {
      toast.error("Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  const validLocations = useMemo(() => locations.filter((l) => l.latitude && l.longitude), [locations]);
  const addressedLocations = useMemo(() => locations.filter((l) => l.address), [locations]);
  const sectorInfo = taxonomy[orgData.sector];
  const subInfo = orgData.sub_industries.length > 0 ? sectorInfo?.sub_industries?.[orgData.sub_industries[0]] : null;
  const ed = enrichment?.enrichment_data || {};
  const cd = enrichment?.classification_data || {};
  const ci = enrichment?.compliance_identifiers || {};
  const oh = enrichment?.org_hierarchy || {};
  const fin = enrichment?.risk_profile_seed?.financial_health_indicator?.status || "insufficient_data";
  const finReady = !!enrichment?.risk_profile_seed?.financial_health_indicator?.status;

  const canProceed = () => {
    switch (currentStep) {
      case 0: return enrichMode === "manual" || enrichStatus === "complete";
      case 1: return !!orgData.sector && orgData.sub_industries.length > 0
        && (!orgData.naics_primary || orgData.naics_primary.length === 6)
        && (!orgData.sic_primary || orgData.sic_primary.length === 4);
      case 2: return !!(orgData.name && orgData.phone && orgData.website && addressedLocations.length > 0);
      case 3: return !!hierarchy.selected;
      case 4: return true;
      case 5: return departments.length > 0;
      case 6: return true;
      default: return false;
    }
  };

  const goNext = async () => {
    if (currentStep === 3) await persistStructure();
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  /* ── Start Over (Step 1 / Identity only) — clears all entered data and
        enrichment results back to a fresh empty wizard state. Also asks the
        backend to wipe enrichment + AI-seeded records for this org. ── */
  const resetWizard = async () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    // Clear enrichment + AI-seeded records server-side (best-effort).
    if (orgId) {
      try {
        const res = await api.post(`/enrichment/clear/${orgId}`);
        console.log("[StartOver] enrichment cleared:", res.data);
        clearEnrichmentContext(orgId);
        toast.success("Organization data cleared. Enter a new domain to begin.");
      } catch (e) {
        console.error("[StartOver] enrichment clear failed", e);
        toast.error("Could not clear previous auto-discovery");
      }
    }
    triggeredRef.current = false;
    lastCountRef.current = 0;
    touchedRef.current = {};
    setEnrichMode(null);
    setEnrichStatus("idle");
    setEnrichProgress([]);
    setSourceStatus(null);
    setEnrichment(null);
    setOrgData({
      name: "", sector: "", sub_industry: "", sub_industries: [], phone: "", website: "",
      entity_type: "", duns_number: "", year_founded: "", company_summary: "", parent_company: "",
    });
    setLocations([{ id: 1, name: "Headquarters", address: "", latitude: null, longitude: null, type: "headquarters" }]);
    setLocationRisks({});
    setDbaNames([]); setProducts([]); setSubsidiaries([]); setDivisions([]);
    setNewDba(""); setNewProduct(""); setNewSub(""); setNewDivision("");
    setDepartments([]); setSuggestedDepartments([]); setDeptInitialized(false);
    setAcceptedFunctions([]); setAcceptedMissionVision([]);
    setAcceptedStakeholders([]); setAcceptedInfrastructure([]);
    setAiRecon(null); setAiReconLoading(false);
    setHierarchy({ candidates: [], selected: "", loading: false });
    setFrameworks(null); setFwLoading(false);
    setSubInd({ loading: false, rationale: "" });
    setCurrentStep(0);
    toast.success("Wizard reset — start fresh");
  };

  /* ── Per-category "Clear All" for accepted AI suggestions (Step 6) ── */
  const clearAcceptedCategory = async (category, setter, count, label) => {
    if (!window.confirm(`Clear all ${count} ${label}? This removes AI-generated items. Manually created items are preserved.`)) return;
    setter([]);
    if (orgId) {
      try { await api.post(`/enrichment/clear/${orgId}?category=${category}`); }
      catch (e) { console.error(`[ClearAll:${category}] failed`, e); }
    }
    toast.success(`Cleared ${count} ${label}`);
  };

  // Generic case-insensitive add/remove for the editable enrichment lists.
  const addItem = (list, setList, key, value, clear) => {
    const v = (value || "").trim();
    if (!v) { clear(); return; }
    if (!list.some((x) => String(x).toLowerCase() === v.toLowerCase())) {
      markTouched(key);
      setList([...list, v]);
    }
    clear();
  };
  const removeItem = (list, setList, key, idx) => {
    markTouched(key);
    setList(list.filter((_, i) => i !== idx));
  };

  // Render a short, human label for an accepted AI module suggestion (string or object).
  const labelForExtra = (x) => {
    if (x == null) return "";
    if (typeof x === "string") return x.replace(/^(mission|vision):\s*/i, (m) => m.toUpperCase());
    if (x.mission) return `Mission: ${x.mission}`;
    if (x.vision) return `Vision: ${x.vision}`;
    return x.name || x.label || x.role || x.title || JSON.stringify(x).slice(0, 48);
  };

  return (
    <Card className="max-w-4xl mx-auto" data-testid="smart-onboarding-wizard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle>AI-Powered Organization Setup</CardTitle>
              <CardDescription>Set up your organization — enter details yourself or auto-discover them</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="bg-sky-50 text-sky-700">Step {currentStep + 1} of {STEPS.length}</Badge>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-between mt-6 overflow-x-auto pb-1">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center shrink-0">
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
                  currentStep > step.id ? "bg-emerald-500 text-white"
                  : currentStep === step.id ? "bg-sky-700 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id + 1}
                </div>
                <span className="text-[10px] font-medium mt-1 text-slate-600 whitespace-nowrap">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-8 md:w-12 h-1 mx-1.5 rounded ${currentStep > step.id ? "bg-emerald-500" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ═══ Step 0 — Organization Identity ═══ */}
        {currentStep === 0 && (
          <div className="space-y-5" data-testid="step-identity">
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-sky-600" />
                <h3 className="font-semibold text-slate-900">Set Up Your Organization</h3>
              </div>
              <p className="text-sm text-slate-500">
                Enter your details yourself, or let us pre-fill them from public business data
                (SEC EDGAR, ProPublica and more). Everything is editable.
              </p>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="org_name">Organization Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="org_name" className={`pl-10 ${step0Errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}`} value={orgData.name}
                    onChange={(e) => { setOrgData({ ...orgData, name: e.target.value }); if (step0Errors.name) setStep0Errors((p) => ({ ...p, name: undefined })); }}
                    placeholder="Acme Health Systems" data-testid="org-name-input"
                    disabled={enrichStatus === "running"} />
                </div>
                {step0Errors.name && <p className="text-xs text-red-600" data-testid="org-name-error">{step0Errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="org_website">Company Website URL *</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="org_website" className={`pl-10 ${step0Errors.website ? "border-red-500 focus-visible:ring-red-500" : ""}`} value={orgData.website}
                    onChange={(e) => { setOrgData({ ...orgData, website: e.target.value }); if (step0Errors.website) setStep0Errors((p) => ({ ...p, website: undefined })); }}
                    placeholder="acmehealth.com" data-testid="org-website-input"
                    disabled={enrichStatus === "running"} />
                </div>
                {step0Errors.website
                  ? <p className="text-xs text-red-600" data-testid="org-website-error">{step0Errors.website}</p>
                  : <p className="text-xs text-slate-400">Then choose how you'd like to continue.</p>}
              </div>

              {enrichStatus === "idle" && enrichMode !== "manual" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="identity-choice">
                  <button type="button"
                    onMouseDown={() => { skipRef.current = true; }}
                    onClick={() => { setIdentityChoice("manual"); chooseManual(); }}
                    className={`text-left rounded-lg border-2 bg-white p-3.5 transition-all ${identityChoice === "manual" ? "border-sky-700 ring-1 ring-sky-200" : "border-slate-200 hover:border-slate-400"}`}
                    data-testid="enter-details-myself-btn">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-800"><Pencil className="w-4 h-4" /> Enter details myself</span>
                    <span className="block text-xs text-slate-500 mt-0.5">Fill in your organization manually — no lookup.</span>
                  </button>
                  <button type="button"
                    onClick={() => { setIdentityChoice("auto"); startEnrichment(); }}
                    className={`text-left rounded-lg border-2 bg-white p-3.5 transition-all ${identityChoice === "auto" ? "border-sky-700 ring-1 ring-sky-200" : "border-slate-200 hover:border-slate-400"}`}
                    data-testid="enrich-start-btn">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-800"><Sparkles className="w-4 h-4" /> Auto-discover from website</span>
                    <span className="block text-xs text-slate-500 mt-0.5">We pull public business data to pre-fill everything.</span>
                  </button>
                </div>
              )}

              {/* Manual mode: optional one-click auto-fill (still user-reviewed). */}
              {enrichMode === "manual" && enrichStatus === "idle" && (
                <div className="pt-1">
                  <Button type="button" variant="outline" size="sm"
                    onClick={startEnrichment}
                    className="text-sky-700 border-sky-300 hover:bg-sky-50"
                    data-testid="fill-rest-automatically-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" /> Fill the rest automatically — you review each value
                  </Button>
                </div>
              )}

              {enrichStatus === "running" && (
                <div className="py-3 space-y-2" data-testid="enrich-progress">
                  <div className="flex items-center gap-2 text-sky-700 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Auto-discovering your organization profile…
                  </div>
                  <ul className="text-sm text-slate-500 space-y-1.5 pl-1">
                    {enrichProgress.map((p, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {sourceStatus && (enrichStatus === "running" || enrichStatus === "complete") && (
                <div className="flex flex-wrap gap-2 py-1" data-testid="enrich-source-status">
                  {Object.entries(sourceStatus).map(([src, st]) => {
                    const cfg = st === "ok"
                      ? { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", mark: "✓" }
                      : st === "key_error"
                        ? { cls: "bg-slate-50 text-slate-500 border-slate-200", mark: "not configured" }
                        : { cls: "bg-slate-50 text-slate-500 border-slate-200", mark: "not configured" };
                    return (
                      <span key={src} className={`text-[11px] px-2 py-0.5 rounded-full border ${cfg.cls}`} data-testid={`source-status-${src}`}>
                        {SOURCE_LABELS[src] || src}: {cfg.mark}
                      </span>
                    );
                  })}
                  {Object.values(sourceStatus).some((v) => v === "key_error" || v === "no_key") && (
                    <span className="text-[11px] text-slate-500 w-full">Sources marked “not configured” are optional and are skipped — add a key any time in WebAdmin → Data Sources.</span>
                  )}
                </div>
              )}

              {enrichment && (enrichStatus === "running" || enrichStatus === "complete") && (
                <div className={`space-y-3 rounded-lg border p-4 ${enrichStatus === "complete" ? "border-emerald-200 bg-emerald-50/40" : "border-sky-200 bg-sky-50/40"}`} data-testid="enrich-results">
                  <div className={`flex items-center gap-2 text-sm font-medium ${enrichStatus === "complete" ? "text-emerald-800" : "text-sky-800"}`}>
                    {enrichStatus === "complete"
                      ? <><CheckCircle2 className="w-4 h-4" /> Profile discovered — continue to confirm details</>
                      : <><Loader2 className="w-4 h-4 animate-spin" /> Discovering your profile — details stream in as they arrive…</>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EnrichField icon={Building2} label="Legal Name" value={ed.legal_name || orgData.name} prov={provenance.legal_name} pending={enrichStatus === "running"} attribute="legal_name" />
                    <EnrichField icon={ShieldCheck} label="Entity Type" value={ed.entity_type} prov={provenance.entity_type} pending={enrichStatus === "running"} attribute="entity_type" />
                    <EnrichField icon={Cpu} label="Industry Sector"
                      value={cd.naics_primary ? `NAICS ${cd.naics_primary} · ${cd.naics_to_sector_mapping || ""}` : (cd.sic_primary ? `SIC ${cd.sic_primary} · ${cd.naics_to_sector_mapping || ""}` : "")} prov={provenance.naics_primary} pending={enrichStatus === "running"} attribute="industry_sector" />
                    <EnrichField icon={Users} label="Employees" value={ed.employee_band} prov={provenance.employee_band} pending={enrichStatus === "running"} attribute="employee_size" />
                    <EnrichField icon={MapPin} label="HQ"
                      value={ed.headquarters?.city ? `${ed.headquarters.city}, ${ed.headquarters.state || ""}` : ""} prov={provenance.headquarters} pending={enrichStatus === "running"} attribute="headquarters" />
                  </div>
                  {(ed.tech_stack || []).length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1 flex items-center gap-2">Technology Infrastructure <AttributeInfoIcon attribute="tech_stack" source={(provenance.tech_stack || {}).source} /> <ProvenanceTag prov={provenance.tech_stack} /></p>
                      <div className="flex flex-wrap gap-1.5">
                        {ed.tech_stack.slice(0, 12).map((t) => <Badge key={t} className="bg-slate-100 text-slate-700 text-[11px]">{t}</Badge>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Additive identity details (DBA, entity type, D-U-N-S, year founded) ── */}
              {(enrichStatus === "complete" || enrichMode === "manual") && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4" data-testid="identity-additional-details">
                  <h4 className="text-sm font-semibold text-slate-800">Additional Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Entity Type
                        <SuggestField orgId={orgId} field="entity_type" onAccept={(v) => setOrgData((p) => ({ ...p, entity_type: Array.isArray(v) ? v[0] : String(v) }))} /></Label>
                      <Select value={orgData.entity_type || undefined}
                        onValueChange={(v) => setOrgData({ ...orgData, entity_type: v })}>
                        <SelectTrigger className="h-9" data-testid="entity-type-select"><SelectValue placeholder="Select entity type" /></SelectTrigger>
                        <SelectContent>
                          {Array.from(new Set([...ENTITY_TYPE_OPTIONS, orgData.entity_type].filter(Boolean))).map((et) => (
                            <SelectItem key={et} value={et}>{et}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Year Founded
                        <SuggestField orgId={orgId} field="year_founded" onAccept={(v) => setOrgData((p) => ({ ...p, year_founded: String(v) }))} /></Label>
                      <Input value={orgData.year_founded} onChange={(e) => setOrgData({ ...orgData, year_founded: e.target.value })}
                        placeholder="e.g. 1998" className="h-9" data-testid="year-founded-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">D-U-N-S Number
                        {(touchedRef.current.duns_number || provenance.duns_number?.source === "manual") && orgData.duns_number
                          ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="duns-entered-by-you">Entered by you</span>
                          : <ProvenanceTag prov={provenance.duns_number} />}
                        <SuggestField orgId={orgId} field="duns_number" onAccept={(v) => { markTouched("duns_number"); setOrgData((p) => ({ ...p, duns_number: String(v).replace(/\D/g, "").slice(0, 9) })); }} />
                      </Label>
                      <Input value={orgData.duns_number} inputMode="numeric" maxLength={9}
                        onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 9); markTouched("duns_number"); setOrgData({ ...orgData, duns_number: v }); }}
                        placeholder="9-digit D-U-N-S (if known)" className="h-9" data-testid="duns-input" />
                      {orgData.duns_number && orgData.duns_number.length !== 9 && (
                        <p className="text-[11px] text-red-600" data-testid="duns-error">D-U-N-S must be 9 digits.</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Federal ID (EIN)
                        {(touchedRef.current.ein || provenance.ein?.source === "manual") && orgData.ein
                          ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="ein-entered-by-you">Entered by you</span>
                          : <ProvenanceTag prov={provenance.ein} />}
                        <SuggestField orgId={orgId} field="ein" onAccept={(v) => { markTouched("ein"); const d = String(v).replace(/\D/g, "").slice(0, 9); setOrgData((p) => ({ ...p, ein: d.length > 2 ? d.slice(0, 2) + "-" + d.slice(2) : d })); }} />
                      </Label>
                      <Input value={orgData.ein} inputMode="numeric" maxLength={10}
                        onChange={(e) => { let v = e.target.value.replace(/\D/g, "").slice(0, 9); if (v.length > 2) v = v.slice(0, 2) + "-" + v.slice(2); markTouched("ein"); setOrgData({ ...orgData, ein: v }); }}
                        placeholder="NN-NNNNNNN" className="h-9" data-testid="ein-input" />
                      {orgData.ein && orgData.ein.replace(/\D/g, "").length !== 9 && (
                        <p className="text-[11px] text-red-600" data-testid="ein-error">EIN must be 9 digits.</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-2">DBA / Alternate Names
                      {dbaNames.length > 0 && <span className="text-[10px] text-slate-400 font-normal">via PDL · Wikidata · AI Recon</span>}
                      <SuggestField orgId={orgId} field="dba" onAccept={(v) => setDbaNames(Array.isArray(v) ? v : String(v).split(",").map((x) => x.trim()).filter(Boolean))} />
                    </Label>
                    <EditableChips items={dbaNames} newValue={newDba} setNewValue={setNewDba}
                      onAdd={() => addItem(dbaNames, setDbaNames, "dba", newDba, () => setNewDba(""))}
                      onRemove={(i) => removeItem(dbaNames, setDbaNames, "dba", i)}
                      placeholder="Add a DBA / alternate name" testId="dba-names" emptyHint={null} />
                  </div>
                  {/* B3 — editable Employees + Technology infrastructure */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Number of Employees
                        {touchedRef.current.employee_count && orgData.employee_count
                          ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="employees-entered-by-you">Entered by you</span>
                          : <ProvenanceTag prov={provenance.employee_count} />}
                        <SuggestField orgId={orgId} field="employees" onAccept={(v) => { markTouched("employee_count"); setOrgData((p) => ({ ...p, employee_count: String(v) })); }} />
                      </Label>
                      <Input type="number" value={orgData.employee_count}
                        onChange={(e) => { markTouched("employee_count"); setOrgData({ ...orgData, employee_count: e.target.value }); }}
                        placeholder="e.g. 1200" className="h-9" data-testid="employees-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Technology Infrastructure
                        {touchedRef.current.tech_stack && orgData.tech_stack?.length
                          ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="tech-entered-by-you">Entered by you</span>
                          : <ProvenanceTag prov={provenance.tech_stack} />}
                        <SuggestField orgId={orgId} field="tech_stack" onAccept={(v) => { markTouched("tech_stack"); setOrgData((p) => ({ ...p, tech_stack: Array.isArray(v) ? v : String(v).split(",").map((x) => x.trim()).filter(Boolean) })); }} />
                      </Label>
                      <EditableChips items={orgData.tech_stack} newValue={newTech} setNewValue={setNewTech}
                        onAdd={() => { if (newTech.trim()) { markTouched("tech_stack"); setOrgData((p) => ({ ...p, tech_stack: [...(p.tech_stack || []), newTech.trim()] })); setNewTech(""); } }}
                        onRemove={(i) => { markTouched("tech_stack"); setOrgData((p) => ({ ...p, tech_stack: p.tech_stack.filter((_, x) => x !== i) })); }}
                        placeholder="Add a system e.g. Oracle EBS" testId="tech-stack" emptyHint={null} />
                    </div>
                  </div>
                  {/* B3 — editable annual revenue + fiscal year */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-2">Annual Revenue (USD)
                        {touchedRef.current.annual_revenue && orgData.annual_revenue
                          ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="revenue-entered-by-you">Entered by you</span>
                          : <ProvenanceTag prov={provenance.annual_revenue} />}
                        <SuggestField orgId={orgId} field="revenue" onAccept={(v) => { markTouched("annual_revenue"); setOrgData((p) => ({ ...p, annual_revenue: String(v).replace(/[^0-9.]/g, "") })); }} />
                      </Label>
                      <Input type="number" value={orgData.annual_revenue}
                        onChange={(e) => { markTouched("annual_revenue"); setOrgData({ ...orgData, annual_revenue: e.target.value }); }}
                        placeholder="e.g. 50000000" className="h-9" data-testid="revenue-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Revenue Fiscal Year</Label>
                      <Input type="number" value={orgData.revenue_fiscal_year}
                        onChange={(e) => { markTouched("annual_revenue"); setOrgData({ ...orgData, revenue_fiscal_year: e.target.value }); }}
                        placeholder="e.g. 2025" className="h-9" data-testid="revenue-fy-input" />
                    </div>
                  </div>
                  {/* B2 — certifications */}
                  <div className="space-y-2 mt-3" data-testid="certifications-editor">
                    <Label className="text-xs flex items-center gap-2">Certifications
                      <SuggestField orgId={orgId} field="certifications" onAccept={(v) => { const arr = Array.isArray(v) ? v : []; if (arr.length) setOrgData((p) => ({ ...p, certifications: [...(p.certifications || []), ...arr.map((x) => (typeof x === "string" ? { type: "Other", name: x } : x))] })); }} /></Label>
                    {(orgData.certifications || []).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`cert-row-${i}`}>
                        <span className="font-medium">{c.type === "Other" ? c.name : c.type}</span>
                        {c.certificate_number && <span className="text-slate-500">#{c.certificate_number}</span>}
                        {c.expiry && (isExpired(c.expiry)
                          ? <span className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5" data-testid={`cert-expired-${i}`}>Expired</span>
                          : <span className="text-slate-500">exp {c.expiry}</span>)}
                        <button type="button" className="ml-auto text-red-600" onClick={() => removeCert(i)} data-testid={`cert-remove-${i}`}>✕</button>
                      </div>
                    ))}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <select className="h-9 border border-slate-200 rounded px-2 text-sm" value={certForm.type}
                        onChange={(e) => setCertForm({ ...certForm, type: e.target.value })} data-testid="cert-type-select">
                        {CERT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {certForm.type === "Other" && (
                        <Input value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} placeholder="Name (required)" className="h-9" data-testid="cert-name-input" />
                      )}
                      <Input value={certForm.certificate_number} onChange={(e) => setCertForm({ ...certForm, certificate_number: e.target.value })} placeholder="Certificate # (optional)" className="h-9" data-testid="cert-number-input" />
                      <Input value={certForm.issuing_body} onChange={(e) => setCertForm({ ...certForm, issuing_body: e.target.value })} placeholder="Issuing body (optional)" className="h-9" data-testid="cert-issuer-input" />
                      <Input type="date" value={certForm.expiry} onChange={(e) => setCertForm({ ...certForm, expiry: e.target.value })} className="h-9" data-testid="cert-expiry-input" />
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={addCert} data-testid="cert-add-btn">Add Certification</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ Step 1 — Confirm Industry ═══ */}
        {currentStep === 1 && (
          <div className="space-y-5" data-testid="step-industry">
            {/* Editable primary industry codes (B6) + optional auto-fill (manual mode) */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="industry-codes-editor">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="text-sm font-semibold text-slate-800">Industry Codes</h4>
                {enrichMode === "manual" && enrichStatus === "idle" && (
                  <Button type="button" variant="outline" size="sm" onClick={startEnrichment}
                    className="text-sky-700 border-sky-300 hover:bg-sky-50" data-testid="fill-rest-automatically-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" /> Fill the rest automatically — you review each value
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">Primary NAICS (6 digits)
                    {(touchedRef.current.naics_primary || provenance.naics_primary?.source === "manual")
                      ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="naics-entered-by-you">Entered by you</span>
                      : <ProvenanceTag prov={provenance.naics_primary} />}
                    <SuggestField orgId={orgId} field="naics" onAccept={(v) => { markTouched("naics_primary"); setOrgData((p) => ({ ...p, naics_primary: String(v).replace(/\D/g, "").slice(0, 6) })); }} />
                  </Label>
                  <Input value={orgData.naics_primary} inputMode="numeric" maxLength={6}
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); markTouched("naics_primary"); setOrgData({ ...orgData, naics_primary: v }); }}
                    placeholder="e.g. 541511" className="h-9" data-testid="naics-primary-input" />
                  {orgData.naics_primary && orgData.naics_primary.length !== 6 && (
                    <p className="text-[11px] text-red-600" data-testid="naics-primary-error">NAICS must be 6 digits.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-2">Primary SIC (4 digits)
                    {(touchedRef.current.sic_primary || provenance.sic_primary?.source === "manual")
                      ? <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid="sic-entered-by-you">Entered by you</span>
                      : <ProvenanceTag prov={provenance.sic_primary} />}
                    <SuggestField orgId={orgId} field="sic" onAccept={(v) => { markTouched("sic_primary"); setOrgData((p) => ({ ...p, sic_primary: String(v).replace(/\D/g, "").slice(0, 4) })); }} />
                  </Label>
                  <Input value={orgData.sic_primary} inputMode="numeric" maxLength={4}
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); markTouched("sic_primary"); setOrgData({ ...orgData, sic_primary: v }); }}
                    placeholder="e.g. 7371" className="h-9" data-testid="sic-primary-input" />
                  {orgData.sic_primary && orgData.sic_primary.length !== 4 && (
                    <p className="text-[11px] text-red-600" data-testid="sic-primary-error">SIC must be 4 digits.</p>
                  )}
                </div>
              </div>
            </div>
            {cd.naics_to_sector_mapping && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 flex items-center gap-2 text-sm" data-testid="naics-auto-banner">
                <Sparkles className="w-4 h-4 text-sky-600 shrink-0" />
                <span className="text-slate-700">
                  Auto-detected: {cd.naics_primary ? <>NAICS <strong>{cd.naics_primary}</strong></> : <>SIC <strong>{cd.sic_primary}</strong></>} → <strong>{cd.naics_to_sector_mapping}</strong>
                </span>
                <ProvenanceTag prov={provenance.naics_primary} className="ml-auto" />
              </div>
            )}

            {/* AI-Recon industry fallback (private companies with no NAICS/SIC) */}
            {!cd.naics_to_sector_mapping && (cd.ai_sector || cd.ai_sub_industry) && (
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 flex items-center gap-2 text-sm" data-testid="ai-industry-banner">
                <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
                <span className="text-slate-700">
                  AI-detected industry: <strong>{cd.ai_sector || "—"}</strong>
                  {cd.ai_sub_industry ? <> · <strong>{cd.ai_sub_industry}</strong></> : null}
                </span>
                <ProvenanceTag prov={{ label: "AI Recon" }} className="ml-auto" />
              </div>
            )}

            {/* NAICS full 6-level breakdown — expandable */}
            {(cd.naics_breakdown?.length > 0 || cd.naics_primary) && (
              <div className="rounded-lg border border-slate-200 bg-white" data-testid="naics-detail-panel">
                <button type="button" onClick={() => setNaicsOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm" data-testid="naics-detail-toggle">
                  <span className="flex items-center gap-2 font-medium text-slate-700">
                    <Layers className="w-4 h-4 text-sky-700" /> NAICS {cd.naics_primary || (cd.naics_breakdown?.[0]?.naics_code) || ""}
                    <EnrichmentDataChip value={cd.naics_to_sector_mapping || cd.naics_breakdown?.[0]?.sector} source={(provenance.naics_primary || {}).source} testId="naics-source-chip" attribute="naics_sector" />
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${naicsOpen ? "rotate-180" : ""}`} />
                </button>
                {naicsOpen && cd.naics_breakdown?.length > 0 && (
                  <div className="px-3 pb-3 pt-1 space-y-1 text-xs" data-testid="naics-detail-body">
                    {[
                      ["Sector", cd.naics_breakdown[0].sector],
                      ["Sub-sector", cd.naics_breakdown[0].sub_sector],
                      ["Industry group", cd.naics_breakdown[0].industry_group],
                      ["NAICS industry", cd.naics_breakdown[0].naics_industry],
                      ["National industry", cd.naics_breakdown[0].national_industry],
                    ].filter(([, v]) => v).map(([lvl, v]) => (
                      <div key={lvl} className="flex justify-between gap-4"><span className="text-slate-400">{lvl}</span><span className="text-slate-700 text-right">{v}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SIC full 4-level breakdown — expandable */}
            {(cd.sic_breakdown?.length > 0 || cd.sic_primary) && (
              <div className="rounded-lg border border-slate-200 bg-white" data-testid="sic-detail-panel">
                <button type="button" onClick={() => setSicOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm" data-testid="sic-detail-toggle">
                  <span className="flex items-center gap-2 font-medium text-slate-700">
                    <Layers className="w-4 h-4 text-teal-700" /> SIC {cd.sic_primary || (cd.sic_breakdown?.[0]?.sic_code) || ""}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${sicOpen ? "rotate-180" : ""}`} />
                </button>
                {sicOpen && cd.sic_breakdown?.length > 0 && (
                  <div className="px-3 pb-3 pt-1 space-y-1 text-xs" data-testid="sic-detail-body">
                    {[
                      ["Major group", cd.sic_breakdown[0].major_group],
                      ["Industry group", cd.sic_breakdown[0].industry_group],
                      ["Industry sector", cd.sic_breakdown[0].industry_sector],
                    ].filter(([, v]) => v).map(([lvl, v]) => (
                      <div key={lvl} className="flex justify-between gap-4"><span className="text-slate-400">{lvl}</span><span className="text-slate-700 text-right">{v}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="flex items-center gap-2">Industry Sector *
                <SuggestField orgId={orgId} field="sector" onAccept={(v) => setOrgData((p) => ({ ...p, sector: Array.isArray(v) ? v[0] : String(v) }))} /></Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(taxonomy).map(([sectorId, info]) => {
                  const Icon = SECTOR_ICONS[sectorId] || Briefcase;
                  return (
                    <button key={sectorId} type="button"
                      onClick={() => setOrgData({ ...orgData, sector: sectorId, sub_industry: "", sub_industries: [] })}
                      className={`p-3.5 rounded-lg border-2 text-left transition-all ${orgData.sector === sectorId ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`sector-${sectorId}`}>
                      <Icon className={`w-5 h-5 mb-1.5 ${orgData.sector === sectorId ? "text-sky-700" : "text-slate-400"}`} />
                      <p className="font-medium text-sm">{info.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{Object.keys(info.sub_industries).length} sub-industries</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {orgData.sector && taxonomy[orgData.sector] && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                <Label className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-sky-700" /> Sub-Industry *
                  <SuggestField orgId={orgId} field="sub_industry" onAccept={(v) => setOrgData((p) => ({ ...p, sub_industries: Array.isArray(v) ? v : [String(v)] }))} />
                  <span className="text-xs text-slate-400 font-normal">Select one or more within {taxonomy[orgData.sector]?.label}</span>
                  {enrichment && (
                    <Button type="button" variant="ghost" size="sm"
                      className="ml-auto h-7 px-2 text-xs text-violet-700 hover:bg-violet-50"
                      onClick={() => detectSubIndustry(true)} disabled={subInd.loading}
                      data-testid="rerun-subindustry-btn">
                      {subInd.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Re-run AI Detection
                    </Button>
                  )}
                </Label>
                {(subInd.loading || subInd.rationale) && (
                  <div className="rounded-md border border-violet-200 bg-violet-50/60 p-2.5 flex items-start gap-2 text-xs" data-testid="ai-subindustry-banner">
                    {subInd.loading
                      ? <><Loader2 className="w-3.5 h-3.5 text-violet-600 animate-spin mt-0.5" /> <span className="text-slate-600">AI is detecting your sub-industries…</span></>
                      : <><Sparkles className="w-3.5 h-3.5 text-violet-600 mt-0.5" /> <span className="text-slate-600">AI-detected & pre-selected{subInd.rationale ? `: ${subInd.rationale}` : ""}</span><ProvenanceTag prov={{ label: "AI Recon" }} className="ml-auto shrink-0" /></>}
                  </div>
                )}
                {orgData.sub_industries.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {orgData.sub_industries.map((subId) => {
                      const si = taxonomy[orgData.sector]?.sub_industries?.[subId];
                      return (
                        <Badge key={subId} variant="outline" className="bg-sky-100 text-sky-700 border-sky-300 text-xs pl-2 pr-1 py-1 gap-1">
                          {si?.label || subId}
                          <button type="button" className="ml-0.5 hover:bg-sky-200 rounded-full p-0.5"
                            onClick={() => setOrgData({ ...orgData, sub_industries: orgData.sub_industries.filter((s) => s !== subId) })}>
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(taxonomy[orgData.sector].sub_industries).map(([subId, si]) => {
                    const SubIcon = SUB_INDUSTRY_ICONS[subId] || Briefcase;
                    const selected = orgData.sub_industries.includes(subId);
                    return (
                      <button key={subId} type="button"
                        onClick={() => {
                          const updated = selected ? orgData.sub_industries.filter((s) => s !== subId) : [...orgData.sub_industries, subId];
                          setOrgData({ ...orgData, sub_industries: updated, sub_industry: updated[0] || "" });
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${selected ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                        data-testid={`sub-industry-${subId}`}>
                        <div className="flex items-center gap-2">
                          <SubIcon className={`w-4 h-4 ${selected ? "text-sky-700" : "text-slate-400"}`} />
                          <p className="font-medium text-sm">{si.label}</p>
                          {selected && <div className="ml-auto w-5 h-5 rounded bg-sky-700 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 ml-6">{si.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ Step 2 — Confirm Organization Profile ═══ */}
        {currentStep === 2 && (
          <div className="space-y-5" data-testid="step-profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p_name" className="flex items-center gap-2">Organization Name *
                  <SuggestField orgId={orgId} field="legal_name" onAccept={(v) => setOrgData((p) => ({ ...p, name: Array.isArray(v) ? v[0] : String(v) }))} /></Label>
                <Input id="p_name" value={orgData.name} onChange={(e) => setOrgData({ ...orgData, name: e.target.value })} data-testid="profile-name-input" />
                <ProvenanceTag prov={provenance.legal_name} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_phone" className="flex items-center gap-2">Phone *
                  <SuggestField orgId={orgId} field="phone" onAccept={(v) => setOrgData((p) => ({ ...p, phone: Array.isArray(v) ? v[0] : String(v) }))} /></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="p_phone" className="pl-10" value={orgData.phone} onChange={(e) => setOrgData({ ...orgData, phone: e.target.value })} placeholder="+1 (555) 123-4567" data-testid="profile-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_web">Website *</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="p_web" className="pl-10" value={orgData.website} onChange={(e) => setOrgData({ ...orgData, website: e.target.value })} data-testid="profile-website-input" />
                </div>
              </div>
              {ed.entity_type && (
                <div className="space-y-2">
                  <Label>Entity Type</Label>
                  <div className="h-10 flex items-center px-3 rounded-md border bg-slate-50 text-sm text-slate-700">
                    {ed.entity_type}<ProvenanceTag prov={provenance.entity_type} className="ml-auto" />
                  </div>
                </div>
              )}
            </div>

            {/* AI Recon office suggestions */}
            {(aiReconLoading || aiRecon?.offices?.length > 0) && (
              <div className="rounded-lg border-2 border-sky-200 bg-gradient-to-br from-sky-50 to-indigo-50 p-4" data-testid="ai-recon-panel">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center"><Globe className="w-4 h-4 text-white" /></div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-900">Pre-Verified Office Locations</h4>
                    <p className="text-xs text-slate-500">AI scan of <strong>{orgData.website}</strong> — click to add.</p>
                  </div>
                  {aiReconLoading && <Loader2 className="w-4 h-4 text-sky-600 animate-spin" />}
                </div>
                {aiRecon?.offices?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {aiRecon.offices.slice(0, 6).map((o, i) => (
                      <button key={i} type="button" data-testid={`ai-recon-office-${i}`}
                        className="text-left p-2 rounded-md border border-sky-300 bg-white hover:bg-sky-100 text-xs transition-colors"
                        onClick={() => setLocations([...locations, {
                          id: Math.max(0, ...locations.map((l) => l.id || 0)) + 1,
                          name: `${o.city || "Office"}${o.is_headquarters ? " (HQ)" : ""}`,
                          address: o.address || `${o.city || ""}, ${o.country || ""}`.trim().replace(/^, /, ""),
                          latitude: null, longitude: null, type: o.is_headquarters ? "headquarters" : "branch", source: "ai-recon",
                        }])}>
                        <div className="font-medium text-slate-900">{o.city}{o.is_headquarters && <span className="ml-1 text-sky-700 text-[10px]">HQ</span>}</div>
                        <div className="text-slate-500">{o.country}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">Office Locations <AttributeInfoIcon attribute="operating_locations" /></h3>
                <p className="text-sm text-slate-500">Confirm addresses for geospatial risk analysis</p>
              </div>
              <Button variant="outline" size="sm" onClick={addLocation} data-testid="add-location-btn"><Plus className="w-4 h-4 mr-1" /> Add Location</Button>
            </div>

            <div className="space-y-3">
              {locations.map((location, index) => (
                <div key={location.id} className="p-4 bg-slate-50 rounded-lg border space-y-3 relative" style={{ zIndex: locations.length - index + 10 }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-sky-700" />
                      <Input value={location.name} className="w-40 h-8 text-sm" placeholder="Location name"
                        onChange={(e) => { const u = [...locations]; u[index].name = e.target.value; setLocations(u); }} />
                      <Select value={location.type} onValueChange={(v) => { const u = [...locations]; u[index].type = v; setLocations(u); }}>
                        <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="headquarters">Headquarters</SelectItem>
                          <SelectItem value="branch">Branch Office</SelectItem>
                          <SelectItem value="datacenter">Data Center</SelectItem>
                          <SelectItem value="warehouse">Warehouse</SelectItem>
                          <SelectItem value="manufacturing">Manufacturing Plant</SelectItem>
                          <SelectItem value="retail">Retail Store</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {location.source && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">auto</Badge>}
                    </div>
                    {locations.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeLocation(index)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                  <LocationAutocomplete value={location.address}
                    onChange={(v) => { const u = [...locations]; u[index].address = v; setLocations(u); }}
                    onLocationSelect={(d) => handleLocationSelect(index, d)}
                    placeholder="Start typing address for AI suggestions..." />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`site-fields-${index}`}>
                    <Input value={location.function || ""} className="h-8 text-sm" placeholder="Function (e.g. Primary DC)"
                      onChange={(e) => { const u = [...locations]; u[index].function = e.target.value; setLocations(u); }} data-testid={`site-function-${index}`} />
                    <Input value={location.phone || ""} className="h-8 text-sm" placeholder="Site phone"
                      onChange={(e) => { const u = [...locations]; u[index].phone = e.target.value; setLocations(u); }} data-testid={`site-phone-${index}`} />
                    <Input value={location.street || ""} className="h-8 text-sm" placeholder="Street"
                      onChange={(e) => { const u = [...locations]; u[index].street = e.target.value; setLocations(u); }} data-testid={`site-street-${index}`} />
                    <Input value={location.city || ""} className="h-8 text-sm" placeholder="City"
                      onChange={(e) => { const u = [...locations]; u[index].city = e.target.value; setLocations(u); }} data-testid={`site-city-${index}`} />
                    <Input value={location.state || ""} className="h-8 text-sm" placeholder="State / Province"
                      onChange={(e) => { const u = [...locations]; u[index].state = e.target.value; setLocations(u); }} data-testid={`site-state-${index}`} />
                    <Input value={location.postal_code || ""} className="h-8 text-sm" placeholder="Postal code"
                      onChange={(e) => { const u = [...locations]; u[index].postal_code = e.target.value; setLocations(u); }} data-testid={`site-postal-${index}`} />
                    <Input value={location.country || ""} className="h-8 text-sm" placeholder="Country (required if address entered)"
                      onChange={(e) => { const u = [...locations]; u[index].country = e.target.value; setLocations(u); }} data-testid={`site-country-${index}`} />
                  </div>
                  {locationRisks[location.id] && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={locationRisks[location.id].overall_risk === "high" ? "destructive" : "secondary"}>
                        <AlertTriangle className="w-3 h-3 mr-1" />{locationRisks[location.id].overall_risk} risk
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {validLocations.length > 0 && (
              <GeospatialRiskMap locations={validLocations.map((l) => ({ ...l, risk_analysis: locationRisks[l.id] }))} onAnalyzeLocation={handleLocationRiskAnalysis} height="300px" />
            )}
            {validLocations.length > 0 && <ThreatIntelligencePanel locations={validLocations} />}

            {/* ── Products & Services ── */}
            <div className="space-y-1.5" data-testid="products-services-section">
              <Label className="text-sm flex items-center gap-2">Products &amp; Services <AttributeInfoIcon attribute="products_services" />
                {products.length > 0 && <span className="text-[10px] text-slate-400 font-normal">via PDL · Wikidata · AI Recon</span>}
                <SuggestField orgId={orgId} field="products_services" onAccept={(v) => { const arr = Array.isArray(v) ? v : String(v).split(",").map((x) => x.trim()).filter(Boolean); setProducts(arr); }} />
              </Label>
              <EditableChips items={products} newValue={newProduct} setNewValue={setNewProduct}
                onAdd={() => addItem(products, setProducts, "products", newProduct, () => setNewProduct(""))}
                onRemove={(i) => removeItem(products, setProducts, "products", i)}
                placeholder="Add a product or service" testId="products" emptyHint={null} />
            </div>

            {/* ── Public Revenue (public companies, SEC EDGAR XBRL) ── */}
            {ed.annual_revenue != null && (
              <div className="space-y-1.5" data-testid="public-revenue-section">
                <Label className="text-sm flex items-center gap-2">Public Revenue <AttributeInfoIcon attribute="annual_revenue" source="sec_edgar" /></Label>
                <div>
                  <EnrichmentDataChip
                    value={`${ed.revenue_fiscal_year ? `FY${ed.revenue_fiscal_year}: ` : ""}${formatRevenue(ed.annual_revenue)}`}
                    source="sec_edgar" testId="public-revenue-chip" attribute="annual_revenue" />
                </div>
              </div>
            )}

            {/* ── Company Summary ── */}
            <div className="space-y-1.5" data-testid="company-summary-section">
                <Label className="text-sm flex items-center gap-2">Company Summary <AttributeInfoIcon attribute="company_summary" />
                  <span className="text-[10px] text-slate-400 font-normal">via People Data Labs</span>
                  <SuggestField orgId={orgId} field="company_summary" onAccept={(v) => setOrgData((p) => ({ ...p, company_summary: Array.isArray(v) ? v.join(" ") : String(v) }))} />
                </Label>
                <Textarea value={orgData.company_summary} rows={3}
                  onChange={(e) => setOrgData({ ...orgData, company_summary: e.target.value })}
                  data-testid="company-summary-textarea" />
              </div>
          </div>
        )}

        {/* ═══ Step 3 — Organization Structure ═══ */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="step-structure">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-1"><Network className="w-4 h-4 text-sky-700" /> Detected Organizational Model</h3>
                <p className="text-sm text-slate-500">Confirm how your organization is structured — this drives the invitation flow.</p>
              </div>
              <Button type="button" variant="ghost" size="sm"
                className="shrink-0 h-7 px-2 text-xs text-violet-700 hover:bg-violet-50"
                onClick={() => detectHierarchy()} disabled={hierarchy.loading}
                data-testid="rerun-hierarchy-btn">
                {hierarchy.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                Re-run AI Detection
              </Button>
            </div>

            {hierarchy.loading && (
              <div className="flex items-center gap-2 text-sky-700 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing organizational structure…</div>
            )}

            {!hierarchy.loading && hierarchy.candidates.length > 0 && (
              <div className="grid gap-3" data-testid="hierarchy-candidates">
                {hierarchy.candidates.map((c) => {
                  const selected = hierarchy.selected === c.model;
                  return (
                    <button key={c.model} type="button" onClick={() => setHierarchy((h) => ({ ...h, selected: c.model }))}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${selected ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`hierarchy-model-${c.model}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-sky-700 bg-sky-700" : "border-slate-300"}`}>
                            {selected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <p className="font-medium text-sm">{HIERARCHY_LABELS[c.model] || c.model}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.ai_confirmed && <Badge className="text-[10px] bg-violet-100 text-violet-700 border border-violet-200"><Sparkles className="w-2.5 h-2.5 mr-0.5" />AI</Badge>}
                          <Badge variant="outline" className="text-xs bg-slate-50">{Math.round((c.confidence || 0) * 100)}% match</Badge>
                        </div>
                      </div>
                      {c.rationale && <p className="text-xs text-slate-500 mt-1.5 ml-7">{c.rationale}</p>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Users className="w-4 h-4 text-sky-700" /> Invitation Mode</Label>
              <p className="text-xs text-slate-400">How team members will be invited to participate in the BCM program.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="invitation-modes">
                {INVITATION_MODES.filter((m) => m.id === "ai_suggested" || showAdvancedModes).map((m) => {
                  const selected = invitationMode === m.id;
                  return (
                    <button key={m.id} type="button" onClick={() => setInvitationMode(m.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${selected ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`invitation-mode-${m.id}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${selected ? "border-sky-700 bg-sky-700" : "border-slate-300"}`} />
                        <p className="font-medium text-sm">{m.label}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">{m.desc}</p>
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={() => setShowAdvancedModes((v) => !v)}
                className="text-xs font-medium text-sky-700 hover:text-sky-800 mt-1"
                data-testid="invitation-mode-advanced-toggle">
                {showAdvancedModes ? "Hide advanced options ▴" : "Advanced ▾"}
              </button>
            </div>

            {/* ── Parent Company ── */}
            <div className="space-y-1.5" data-testid="parent-company-section">
              <Label className="text-sm flex items-center gap-2">Parent Company <AttributeInfoIcon attribute="parent_company" />
                {orgData.parent_company && <span className="text-[10px] text-slate-400 font-normal">via Wikidata · AI Recon</span>}
                <SuggestField orgId={orgId} field="parent" onAccept={(v) => setOrgData((p) => ({ ...p, parent_company: Array.isArray(v) ? v[0] : String(v) }))} />
              </Label>
              <Input value={orgData.parent_company} onChange={(e) => setOrgData({ ...orgData, parent_company: e.target.value })}
                placeholder="Parent company (if a subsidiary)" className="h-9" data-testid="parent-company-input" />
            </div>

            {/* ── Subsidiaries ── */}
            <div className="space-y-1.5" data-testid="subsidiaries-section">
              <Label className="text-sm flex items-center gap-2">Subsidiaries &amp; Affiliated Companies <AttributeInfoIcon attribute="subsidiaries" />
                {subsidiaries.length > 0 && <span className="text-[10px] text-slate-400 font-normal">via SEC EDGAR · Wikidata · PDL</span>}
                <SuggestField orgId={orgId} field="subsidiaries" onAccept={(v) => setSubsidiaries(Array.isArray(v) ? v : String(v).split(",").map((x) => x.trim()).filter(Boolean))} />
              </Label>
              <EditableChips items={subsidiaries} newValue={newSub} setNewValue={setNewSub}
                onAdd={() => addItem(subsidiaries, setSubsidiaries, "subs", newSub, () => setNewSub(""))}
                onRemove={(i) => removeItem(subsidiaries, setSubsidiaries, "subs", i)}
                placeholder="Add a subsidiary" testId="subsidiaries" emptyHint={null} />
            </div>

            {/* ── Divisions / Business Units ── */}
            <div className="space-y-1.5" data-testid="divisions-section">
              <Label className="text-sm flex items-center gap-2">Divisions / Business Units
                {divisions.length > 0 && <span className="text-[10px] text-slate-400 font-normal">via AI Recon</span>}
              </Label>
              <EditableChips items={divisions} newValue={newDivision} setNewValue={setNewDivision}
                onAdd={() => addItem(divisions, setDivisions, "divisions", newDivision, () => setNewDivision(""))}
                onRemove={(i) => removeItem(divisions, setDivisions, "divisions", i)}
                placeholder="Add a division" testId="divisions" emptyHint={null} />
            </div>
          </div>
        )}

        {/* ═══ Step 4 — Regulatory Frameworks ═══ */}
        {currentStep === 4 && (
          <div className="space-y-5" data-testid="step-regulatory">
            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-1"><Gavel className="w-4 h-4 text-sky-700" /> Detected Regulatory Frameworks <AttributeInfoIcon attribute="regulatory_frameworks" /></h3>
              <p className="text-sm text-slate-500">Deterministically derived from your NAICS classification & entity profile.</p>
            </div>
            {fwLoading && <div className="flex items-center gap-2 text-sky-700 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Running compliance rules engine…</div>}
            {!fwLoading && frameworks && (
              <div className="space-y-4" data-testid="frameworks-result">
                <FrameworkGroup title="Mandatory" cls="bg-orange-100 text-orange-800 border-orange-200" items={frameworks.mandatory_frameworks} />
                <FrameworkGroup title="Sector-Specific" cls="bg-purple-100 text-purple-800 border-purple-200" items={frameworks.sector_specific} />
                <FrameworkGroup title="Recommended" cls="bg-sky-100 text-sky-800 border-sky-200" items={frameworks.recommended_frameworks} />
                {normalizeRationale(frameworks.rationale).length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><ScrollText className="w-3.5 h-3.5" /> Rationale</p>
                    <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
                      {normalizeRationale(frameworks.rationale).map((r, i) => (
                        <li key={i}><strong className="text-slate-600">{r.framework}:</strong> {r.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-emerald-500" /> via Rules Engine (deterministic, no AI)</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ Step 5 — Review AI Suggestions (Departments) ═══ */}
        {currentStep === 5 && (
          <div className="space-y-4" data-testid="step-ai-suggestions">
            <AISuggestionsBanner module="critical_functions" orgId={orgId} onAccept={handleAcceptFunctions} refreshKey={enrichment?.enrichment_data?.domain || "none"} />

            {acceptedFunctions.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="accepted-functions">
                <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5 mb-2">
                  <CheckCircle2 className="w-4 h-4" /> {acceptedFunctions.length} Critical Business Function{acceptedFunctions.length > 1 ? "s" : ""} accepted
                  <span className="text-xs font-normal text-emerald-600">— saved to your BCM program</span>
                  <Button type="button" size="sm" variant="outline" className="ml-auto h-7 px-2 text-xs" data-testid="clear-all-critical_functions"
                    onClick={() => clearAcceptedCategory("critical_functions", setAcceptedFunctions, acceptedFunctions.length, "critical functions")}>
                    <X className="w-3.5 h-3.5 mr-1" /> Clear All
                  </Button>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {acceptedFunctions.map((f, i) => (
                    <Badge key={i} variant="outline" className="bg-white text-slate-700 text-[11px] pl-2 pr-1 py-1 gap-1" data-testid={`accepted-fn-${i}`}>
                      {f.function_name}
                      {f.estimated_mtpd && <span className="text-emerald-600">· {f.estimated_mtpd}</span>}
                      <button type="button" className="ml-0.5 hover:bg-slate-100 rounded-full p-0.5"
                        onClick={() => setAcceptedFunctions((prev) => prev.filter((_, idx) => idx !== i))}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* ── Additional AI Suggestion Panels (Mission/Vision, Stakeholders, Infrastructure) ── */}
            <div className="space-y-3" data-testid="additional-ai-suggestions">
              {[
                { module: "mission_vision", onAccept: handleAcceptMissionVision, accepted: acceptedMissionVision, setter: setAcceptedMissionVision, label: "Mission & Vision", clearLabel: "missions", testid: "accepted-mission-vision" },
                { module: "stakeholder_register", onAccept: handleAcceptStakeholders, accepted: acceptedStakeholders, setter: setAcceptedStakeholders, label: "Stakeholder", clearLabel: "stakeholders", testid: "accepted-stakeholders" },
                { module: "infrastructure_applications", onAccept: handleAcceptInfrastructure, accepted: acceptedInfrastructure, setter: setAcceptedInfrastructure, label: "Application", clearLabel: "applications", testid: "accepted-infrastructure" },
              ].map((panel) => (
                <div key={panel.module} className="space-y-2">
                  <AISuggestionsBanner module={panel.module} orgId={orgId} onAccept={panel.onAccept} refreshKey={enrichment?.enrichment_data?.domain || "none"} />
                  {panel.accepted.length > 0 && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid={panel.testid}>
                      <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5 mb-2">
                        <CheckCircle2 className="w-4 h-4" /> {panel.accepted.length} {panel.label}{panel.accepted.length > 1 ? "s" : ""} accepted
                        <span className="text-xs font-normal text-emerald-600">— saved to your BCM program</span>
                        <Button type="button" size="sm" variant="outline" className="ml-auto h-7 px-2 text-xs" data-testid={`clear-all-${panel.module}`}
                          onClick={() => clearAcceptedCategory(panel.module, panel.setter, panel.accepted.length, panel.clearLabel)}>
                          <X className="w-3.5 h-3.5 mr-1" /> Clear All
                        </Button>
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {panel.accepted.map((item, i) => (
                          <Badge key={i} variant="outline" className="bg-white text-slate-700 text-[11px] pl-2 pr-1 py-1 gap-1 max-w-full" data-testid={`${panel.testid}-${i}`}>
                            <span className="truncate max-w-[280px]">{labelForExtra(item)}</span>
                            <button type="button" className="ml-0.5 hover:bg-slate-100 rounded-full p-0.5"
                              onClick={() => panel.setter((prev) => prev.filter((_, idx) => idx !== i))}>
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-700" /> AI-Suggested Departments</h3>
                <p className="text-sm text-slate-500">Based on {subInfo?.label || sectorInfo?.label || "your"} industry</p>
              </div>
              {aiLoading && <Loader2 className="w-5 h-5 animate-spin text-sky-700" />}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {suggestedDepartments.map((dept) => {
                const selected = departments.some((d) => d.name === dept.name);
                return (
                  <button key={dept.name} type="button" onClick={() => toggleDepartment(dept)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${selected ? "border-sky-700 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`dept-${dept.name}`}>
                    <div className="flex items-start justify-between">
                      <p className="font-medium text-sm flex-1">{dept.name}</p>
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${selected ? "bg-sky-700 border-sky-700" : "border-slate-300"}`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{dept.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={`text-xs ${dept.criticality === "critical" ? "bg-red-50 text-red-700 border-red-200" : dept.criticality === "high" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50"}`}>{dept.criticality}</Badge>
                      {dept.suggested_rto && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="w-3 h-3" /> RTO: {dept.suggested_rto}</span>}
                    </div>
                  </button>
                );
              })}
              <div className="p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-sky-400 transition-all">
                <div className="flex items-center gap-2 mb-2"><Plus className="w-4 h-4 text-slate-400" /><span className="font-medium text-sm text-slate-600">Add Custom</span></div>
                <Input placeholder="Department name" className="h-8 text-sm mb-2" id="new-dept-input" data-testid="add-other-dept-input" />
                <Button size="sm" variant="outline" className="w-full" data-testid="add-other-dept-btn"
                  onClick={() => { const el = document.getElementById("new-dept-input"); const v = el?.value?.trim(); if (v) { setDepartments((p) => [...p, { name: v, description: "Custom department", criticality: "medium", selected: true }]); el.value = ""; } }}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
            <p className="text-sm text-slate-600 pt-2 border-t"><strong>{departments.length}</strong> departments selected</p>
          </div>
        )}

        {/* ═══ Step 6 — BCM Program Ready ═══ */}
        {currentStep === 6 && (
          <div className="space-y-6" data-testid="step-ready">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Your BCM Program is ready to launch</span>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-sky-700" /> Organization</h4>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium">{orgData.name}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge variant="outline" className="bg-sky-50 text-sky-700 text-xs">{sectorInfo?.label || orgData.sector}</Badge>
                  {orgData.sub_industries.map((subId) => {
                    const info = taxonomy[orgData.sector]?.sub_industries?.[subId];
                    return info ? <Badge key={subId} variant="outline" className="text-xs">{info.label}</Badge> : null;
                  })}
                  {hierarchy.selected && <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700"><Network className="w-3 h-3 mr-1" />{HIERARCHY_LABELS[hierarchy.selected] || hierarchy.selected}</Badge>}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-700" /> Locations ({addressedLocations.length})</h4>
              <div className="grid gap-2">
                {addressedLocations.map((loc) => (
                  <div key={loc.id} className="bg-slate-50 rounded-lg p-3"><p className="font-medium text-sm">{loc.name}</p><p className="text-xs text-slate-600">{loc.address}</p></div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-sky-700" /> Departments ({departments.length})</h4>
              <div className="flex flex-wrap gap-2">
                {departments.map((d) => <Badge key={d.name} variant="outline" className="bg-white">{d.name}{d.suggested_rto && <span className="ml-1 text-sky-600 font-normal">RTO: {d.suggested_rto}</span>}</Badge>)}
              </div>
            </div>
            {acceptedFunctions.length > 0 && (
              <div className="space-y-2" data-testid="review-critical-functions">
                <h4 className="font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-sky-700" /> Critical Business Functions ({acceptedFunctions.length})</h4>
                <div className="flex flex-wrap gap-1.5">
                  {acceptedFunctions.map((f, i) => <Badge key={i} variant="outline" className="bg-white text-[11px]">{f.function_name}{f.estimated_mtpd && <span className="ml-1 text-emerald-600 font-normal">{f.estimated_mtpd}</span>}</Badge>)}
                </div>
              </div>
            )}
            {frameworks && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2"><Gavel className="w-4 h-4 text-sky-700" /> Frameworks</h4>
                <div className="flex flex-wrap gap-1.5">
                  {[...(frameworks.mandatory_frameworks || []), ...(frameworks.sector_specific || [])].map((fw) => <Badge key={fw} className="bg-orange-100 text-orange-800 text-[11px]">{fw}</Badge>)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ Navigation ═══ */}
        <div className="flex items-center justify-between pt-4 border-t">
          {currentStep === 0 ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="wizard-start-over-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Start Over
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="start-over-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Start over?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear your organization profile, enrichment data, and all
                    previously accepted AI suggestions
                    ({acceptedFunctions.length} critical functions, {acceptedMissionVision.length} missions, {acceptedStakeholders.length} stakeholders, {acceptedInfrastructure.length} applications).
                    Manually created records are preserved. Start over?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="start-over-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={resetWizard} data-testid="start-over-confirm-btn">Start Over</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button variant="outline" onClick={() => setCurrentStep((s) => Math.max(s - 1, 0))} data-testid="wizard-prev-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={goNext} disabled={!canProceed()} data-testid="wizard-next-btn">
              {currentStep === 0 ? "Continue" : "Next"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleComplete} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-complete-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Complete Setup
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EditableChips({ items, onRemove, onAdd, newValue, setNewValue, placeholder, testId, emptyHint }) {
  return (
    <div data-testid={testId}>
      {(items.length > 0 || emptyHint) && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {items.map((it, i) => (
            <Badge key={`${it}-${i}`} variant="outline" className="bg-sky-100 text-sky-700 border-sky-300 text-xs pl-2 pr-1 py-1 gap-1" data-testid={`${testId}-chip-${i}`}>
              <span className="truncate max-w-[220px]">{it}</span>
              <button type="button" className="ml-0.5 hover:bg-sky-200 rounded-full p-0.5" onClick={() => onRemove(i)} data-testid={`${testId}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {items.length === 0 && emptyHint && <span className="text-xs text-slate-400">{emptyHint}</span>}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={placeholder} className="h-8 text-sm"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} data-testid={`${testId}-input`} />
        <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid={`${testId}-add-btn`}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function EnrichField({ icon: Icon, label, value, prov, pending, attribute }) {
  const empty = value == null || value === "" || value === "—";
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 flex items-center gap-1">{label}
          {attribute && <AttributeInfoIcon attribute={attribute} source={prov?.source} />}
        </p>
        {empty && pending
          ? <span className="inline-block h-4 w-24 rounded bg-slate-200 animate-pulse mt-1" data-testid="enrich-field-pending" />
          : <p className="text-sm text-slate-800 truncate">{empty ? "—" : value}</p>}
        <ProvenanceTag prov={prov} className="block mt-0.5" />
      </div>
    </div>
  );
}

function FrameworkGroup({ title, cls, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">{title} ({items.length})</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((fw) => <Badge key={fw} className={`${cls} border text-[11px]`}>{fw}</Badge>)}
      </div>
    </div>
  );
}

/** Backend returns `rationale` as either an array or a {framework: reason} object. */
function normalizeRationale(rationale) {
  if (!rationale) return [];
  if (Array.isArray(rationale)) {
    return rationale.map((r) => (typeof r === "string" ? { framework: "", reason: r } : { framework: r.framework || "", reason: r.reason || "" }));
  }
  return Object.entries(rationale).map(([framework, reason]) => ({ framework, reason: String(reason) }));
}